import { FC, ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { cn } from 'src/utils/style'
import { Spinner } from 'src/components/spinner'
import { Button } from 'src/components/button'
import type { OrganizationShape } from 'src/services/user/models'

interface OrganizationStripProps {
  organizations: OrganizationShape[]
  /** Per-org property counts for the default badge. Omit when supplying `renderBadge`. */
  propertyCountByOrg?: Record<string, number>
  selectedOrgId: string | null
  onSelect: (orgId: string) => void
  isLoading: boolean
  /** Callback ref attached to the trailing sentinel for IntersectionObserver-driven pagination */
  sentinelRef?: (node: HTMLDivElement | null) => void
  /** True while a "next page" fetch is in flight (renders a trailing spinner) */
  isFetchingNext?: boolean
  /** True if there is another page available; controls whether the sentinel is rendered */
  hasMore?: boolean
  /**
   * Render the badge/status row under each org name. Lets callers swap the
   * default property-count pill for a different indicator (e.g. chat number
   * status) while reusing the same card strip. `isActive` is the selected state
   * of that card so the badge can match the card's emphasis.
   */
  renderBadge?: (org: OrganizationShape, isActive: boolean) => ReactNode
  /** Overrides the data-testid suffix so multiple strips can coexist on a page. */
  testIdPrefix?: string
}

export const OrganizationStrip: FC<OrganizationStripProps> = ({
  organizations,
  propertyCountByOrg,
  selectedOrgId,
  onSelect,
  isLoading,
  sentinelRef,
  isFetchingNext,
  hasMore,
  renderBadge,
  testIdPrefix = 'organization-strip',
}) => {
  const { t } = useTranslation()
  const [accordionOpen, setAccordionOpen] = useState(false)

  // Default badge: a property-count pill matching the card's selected emphasis.
  const badge =
    renderBadge ??
    ((org: OrganizationShape, isActive: boolean) => (
      <span
        className={cn([
          'self-start rounded-full px-2 py-0.5 text-xs',
          isActive ? 'bg-primary text-white' : 'bg-gray-100 text-text-secondary',
        ])}
      >
        {t('reservations.propertyCountOther', { count: propertyCountByOrg?.[org.id] ?? 0 })}
      </span>
    ))

  if (isLoading && organizations.length === 0) {
    return (
      <div className="flex items-center justify-center py-4">
        <Spinner size="sm" />
      </div>
    )
  }

  const selectedOrg = organizations.find((o) => o.id === selectedOrgId) ?? null
  const accordionLabel = selectedOrg
    ? `${t('reservations.organizationAccordionLabel')}: ${selectedOrg.name}`
    : t('reservations.chooseOrganization')

  const handleAccordionSelect = (orgId: string) => {
    onSelect(orgId)
    setAccordionOpen(false)
  }

  return (
    <>
      {/* Mobile: collapsible accordion */}
      <div className="md:hidden" data-testid={`${testIdPrefix}-mobile`}>
        <Button
          variant="unstyled"
          type="button"
          aria-expanded={accordionOpen}
          aria-controls="organization-accordion-list"
          onClick={() => setAccordionOpen((open) => !open)}
          className="flex w-full items-center justify-between rounded-xl border border-border bg-surface p-4 text-left"
        >
          <span className="font-semibold text-text-primary">{accordionLabel}</span>
          <ChevronDownIcon
            className={cn([
              'h-5 w-5 text-text-placeholder motion-safe:transition-transform',
              accordionOpen && 'rotate-180',
            ])}
            aria-hidden="true"
          />
        </Button>
        {accordionOpen ? (
          <ul
            id="organization-accordion-list"
            role="tablist"
            aria-label={t('reservations.organizationsLabel')}
            className="mt-2 flex flex-col gap-2"
          >
            {organizations.map((org) => {
              const isActive = org.id === selectedOrgId
              return (
                <li key={org.id}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => handleAccordionSelect(org.id)}
                    className={cn([
                      'flex w-full flex-col gap-1 rounded-xl border p-4 text-left',
                      isActive
                        ? 'border-primary bg-surface'
                        : 'border-border bg-surface hover:bg-surface-hover',
                    ])}
                  >
                    <span className="font-semibold text-text-primary">{org.name}</span>
                    {badge(org, isActive)}
                  </button>
                </li>
              )
            })}
          </ul>
        ) : null}
      </div>

      {/* Tablet & desktop: horizontal scrollable strip */}
      <div
        className="hidden snap-x snap-mandatory items-stretch gap-3 overflow-x-auto pb-2 md:flex"
        role="tablist"
        aria-label={t('reservations.organizationsLabel')}
        data-testid={`${testIdPrefix}-desktop`}
      >
        {organizations.map((org) => {
          const isActive = org.id === selectedOrgId
          return (
            <button
              key={org.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onSelect(org.id)}
              className={cn([
                'flex min-w-[220px] shrink-0 snap-start flex-col gap-1 rounded-xl border p-4 text-left transition-colors',
                isActive
                  ? 'border-primary bg-surface'
                  : 'border-border bg-surface hover:bg-surface-hover',
              ])}
            >
              <span className="font-semibold text-text-primary">{org.name}</span>
              {badge(org, isActive)}
            </button>
          )
        })}
        {hasMore ? (
          <div
            ref={sentinelRef}
            data-testid={`${testIdPrefix}-sentinel`}
            aria-hidden="true"
            className="flex min-w-[60px] shrink-0 items-center justify-center"
          >
            {isFetchingNext ? <Spinner size="sm" /> : null}
          </div>
        ) : null}
      </div>
    </>
  )
}
